import { useState, useEffect } from 'react';
import { useSEO, generateOrganizationSchema, generateServiceSchema, generateFAQSchema, generateWebPageSchema } from '../../utils/seo';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced'>('basic');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    industry: '',
    employeeSize: '',
    participants: '',
    preferredDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // SEO設定
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: '大阪府補助金で実現する企業AI研修サービス | 株式会社空間Lab',
    description: '大阪府補助金で実現する企業AI研修サービス。補助率75%で実質23.75万円から。8時間でChatGPT・Geminiを使った業務効率化とアプリ生成を習得。株式会社空間Labが提供する実践型AI研修で、社内のAI活用を加速させます。',
    keywords: 'AI研修,大阪府補助金,企業研修,ChatGPT研修,Gemini研修,リスキリング,業務効率化,空間Lab',
    canonical: `${siteUrl}/`,
    ogTitle: '大阪府補助金で実現する企業AI研修サービス | 株式会社空間Lab',
    ogDescription: '補助率75%で実質23.75万円から。8時間でChatGPT・Geminiを使った業務効率化とアプリ生成を習得できる企業向けAI研修。',
    ogImage: `${siteUrl}/og-image.jpg`,
    ogUrl: `${siteUrl}/`,
    schemaData: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(),
        generateOrganizationSchema(),
        generateServiceSchema(),
        generateFAQSchema()
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('company', formData.company);
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('industry', formData.industry);
      formBody.append('employeeSize', formData.employeeSize);
      formBody.append('participants', formData.participants);
      formBody.append('preferredDate', formData.preferredDate);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5732vuisj01hmefqhrg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          industry: '',
          employeeSize: '',
          participants: '',
          preferredDate: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const testimonials = [
    { industry: '製造業', role: '総務（課長）', text: 'AIは触っていたつもりが"使い方"が違いました。8時間で社内の定型業務が自動化でき、提案資料の質も上がりました。' },
    { industry: '建設業', role: '営業（主任）', text: '見積・提案書作成が速くなり、商談準備のストレスが激減。現場で使えるプロンプト設計が一番の収穫でした。' },
    { industry: '飲食業', role: '店舗責任者', text: '売上や仕入れの見方が変わりました。AIでメニュー改善案まで出せて、数字が苦手でも意思決定がラクになりました。' },
    { industry: '美容サロン', role: 'オーナー', text: '予約・SNS投稿・顧客対応の"迷い"が消えました。自分の言葉を保ったまま文章が作れるので、発信が続きます。' },
    { industry: '小売業', role: 'EC担当', text: '商品説明・バナー文言・問い合わせ対応が一気に整いました。属人化していた作業がチームで回る仕組みになりました。' },
    { industry: '士業', role: '事務所代表', text: '文章の精度が上がり、確認時間が短縮。『何をAIに任せ、何を人が判断するか』が整理できたのが大きいです。' },
    { industry: '医療', role: 'クリニック事務長', text: '院内マニュアルや案内文が統一され、問い合わせの質が改善。スタッフ教育にもAIを使える状態になりました。' },
    { industry: '介護', role: '管理者', text: '記録や報告書が早くなり、残業が減りました。現場の言い回しに合わせたテンプレ化ができ、定着しやすいです。' },
    { industry: '不動産', role: '営業（店長）', text: '提案の切り口が増え、説明がわかりやすくなりました。お客様ごとの"刺さる言葉"を作れるので成約率に効きます。' },
    { industry: '物流', role: '業務改善担当', text: '棚卸・在庫・日報などのデータ整理が進み、会議資料が秒速で作れるように。次の改善テーマまで見える化できました。' },
    { industry: '人材', role: '採用担当', text: '求人票・面接質問・評価コメントが整い、採用のブレが減りました。社内ルールに沿った文章生成が実用的でした。' },
    { industry: 'IT', role: 'プロジェクトマネージャー', text: '"AIを使う"から"AIで設計する"へ視点が変化。ChatGPT/Gemini上で小さな社内アプリを作れたのが決定打です。' }
  ];

  const faqs = [
    {
      q: '補助金が使える条件は？',
      a: '大阪府内に事業所を持つ中小企業が対象です。従業員のスキルアップを目的とした研修に対し、補助率75%（上限あり）が適用されます。詳細な条件は無料相談時にご説明いたします。'
    },
    {
      q: '社内にIT担当がいなくても大丈夫？',
      a: '全く問題ありません。本研修は「ITが苦手な方」でも実務で使えることを前提に設計しています。専門用語を使わず、業務の流れに沿って学べるため、どなたでも安心して受講いただけます。'
    },
    {
      q: '研修後に社内展開する方法は？',
      a: '研修では「社内で教えられる資料」もお渡しします。また、1ヶ月間のメールサポート期間中に、社内展開の進め方もアドバイスいたします。'
    },
    {
      q: '何を事前に準備すればいい？',
      a: '特別な準備は不要です。事前ヒアリングで御社の業務内容をお聞きし、最適なカリキュラムを組み立てます。当日はノートPCとインターネット環境があれば受講可能です。'
    },
    {
      q: 'オンライン・対面どちらで受講できますか？',
      a: '両方に対応しています。オンライン（Zoom等）でも対面でも、内容・サポートは同じです。御社のご都合に合わせてお選びいただけます。'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/fb59e5af169faed257c467831b86fc31/6b5b44d6abb76df92ded18f8f997228f.png" 
                alt="株式会社空間Lab" 
                className="h-12 w-auto"
              />
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:0661231122" className="hidden md:flex items-center gap-2 text-gray-700 hover:text-teal-600 transition-colors">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <span className="text-sm font-medium">06-6123-1122</span>
                <span className="text-xs text-gray-500">平日9:00-18:00</span>
              </a>
              <button 
                onClick={scrollToForm}
                className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
              >
                無料相談する
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-emerald-50"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </div>
              <span>申請期限：令和8年3月9日まで（予算上限で早期終了の可能性あり）</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              大阪府補助金で実現する<br />
              <span className="text-teal-600">たった8時間</span>で業務が変わる<br />
              企業AI研修サービス
            </h1>
            
            <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
              AIの能力を50〜80％引き出す「空間Labメソッド」で<br className="hidden md:block" />
              ChatGPT・Geminiを使った実務アプリ生成まで習得
            </p>

            <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-10">
              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-teal-500">
                <div className="text-sm text-gray-600 mb-2">1-5名の場合</div>
                <div className="text-3xl font-bold text-gray-900 mb-1">23.75万円</div>
                <div className="text-sm text-gray-500 line-through">通常95万円</div>
                <div className="mt-3 inline-block bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-xs font-medium">
                  補助率75%適用
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-emerald-500">
                <div className="text-sm text-gray-600 mb-2">6-10名の場合</div>
                <div className="text-3xl font-bold text-gray-900 mb-1">33.75万円</div>
                <div className="text-sm text-gray-500 line-through">通常135万円</div>
                <div className="mt-3 inline-block bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-medium">
                  補助率75%適用
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <button 
                onClick={scrollToForm}
                className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-colors shadow-lg hover:shadow-xl whitespace-nowrap w-full sm:w-auto"
              >
                無料相談する
              </button>
              <button 
                onClick={scrollToForm}
                className="bg-white hover:bg-gray-50 text-teal-600 border-2 border-teal-600 px-8 py-4 rounded-lg text-lg font-bold transition-colors whitespace-nowrap w-full sm:w-auto"
              >
                補助金の適用可否を相談
              </button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-teal-600">
                  <i className="ri-check-line text-lg font-bold"></i>
                </div>
                <span>補助金申請サポート無料</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-teal-600">
                  <i className="ri-check-line text-lg font-bold"></i>
                </div>
                <span>事前ヒアリング無料</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-teal-600">
                  <i className="ri-check-line text-lg font-bold"></i>
                </div>
                <span>研修後1ヶ月メールサポート</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              こんなお悩み、ありませんか？
            </h2>
            <p className="text-lg text-gray-600">
              多くの企業が抱える業務課題をAIで解決します
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'ri-file-text-line', title: '資料作成に時間がかかる', desc: '提案書や報告書の作成に何時間もかかってしまう' },
              { icon: 'ri-customer-service-line', title: '問い合わせ対応が属人化', desc: '担当者によって回答の質がバラバラ' },
              { icon: 'ri-team-line', title: '業務の標準化ができない', desc: 'ノウハウが個人に依存し、共有できていない' },
              { icon: 'ri-user-search-line', title: '採用・教育に手が回らない', desc: '人材育成の時間とコストが負担になっている' },
              { icon: 'ri-line-chart-line', title: '営業活動の効率が悪い', desc: '提案の質を上げたいが、時間が足りない' },
              { icon: 'ri-database-line', title: 'データ活用ができていない', desc: '数字はあるが、分析や意思決定に活かせない' }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg mb-4">
                  <i className={`${item.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl p-8 md:p-12 text-white text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              多くの方がAIを使っているとはいえ、<br className="hidden md:block" />
              AIの能力を人間の脳のように<span className="text-yellow-300">3〜10％しか引き出せていません</span>
            </h3>
            <p className="text-lg md:text-xl mb-6 leading-relaxed">
              本研修を受けると、たった8時間で<strong>"どんなAIでも"50〜80％の能力を引き出す</strong>考え方・使い方を学べます。<br />
              しかもこの引き出し方は<strong>『空間Labメソッド』</strong>で学べば忘れにくく、再現性があります。
            </p>
            <button 
              onClick={scrollToForm}
              className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-bold transition-colors shadow-lg whitespace-nowrap"
            >
              今すぐ無料相談する
            </button>
          </div>
        </div>
      </section>

      {/* What You Can Do Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              8時間で<span className="text-teal-600">できるようになること</span>
            </h2>
            <p className="text-lg text-gray-600">
              実務で即活用できる具体的なスキルを習得
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white rounded-lg">
                  <i className="ri-file-edit-line text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">文書・資料作成の自動化</h3>
              </div>
              <ul className="space-y-3">
                {[
                  '社内向けテンプレート・マニュアルの作成',
                  '議事録から要点抽出→ToDoリスト化',
                  '提案書・見積書の叩き台生成',
                  '顧客対応文の作成（メール・チャット）',
                  '分析レポートの要点整理'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5">
                      <i className="ri-check-line text-lg font-bold"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 text-white rounded-lg">
                  <i className="ri-apps-line text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">業務アプリの作成</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'ChatGPT/Gemini上で動く小さな業務アプリ',
                  '入力フォーム→自動出力のワークフロー',
                  '社内専用の質問応答システム',
                  'データ入力→分析結果の自動生成',
                  '繰り返し業務の自動化ツール'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center text-emerald-600 mt-0.5">
                      <i className="ri-check-line text-lg font-bold"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-gray-900 text-white rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-yellow-500 rounded-lg">
                <i className="ri-lightbulb-line text-xl text-gray-900"></i>
              </div>
              <h3 className="text-2xl font-bold">重要なポイント</h3>
            </div>
            <p className="text-lg leading-relaxed">
              これらのスキルは<strong className="text-yellow-300">「ChatGPT」「Gemini」だけでなく、どんなAIツールでも応用可能</strong>です。<br />
              空間Labメソッドで学ぶ「AI能力の引き出し方」は、ツールが変わっても使える普遍的な考え方です。
            </p>
          </div>
        </div>
      </section>

      {/* Curriculum & Method Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              カリキュラムと空間Labメソッド
            </h2>
            <p className="text-lg text-gray-600">
              基礎から応用まで、御社専用にカスタマイズした8時間研修
            </p>
          </div>

          {/* Curriculum Tabs */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="flex gap-2 mb-8 bg-white p-1 rounded-full shadow-md w-fit mx-auto">
              <button
                onClick={() => setActiveTab('basic')}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'basic'
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                基礎編（5時間）
              </button>
              <button
                onClick={() => setActiveTab('advanced')}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'advanced'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                実践応用編（3時間）
              </button>
            </div>

            {activeTab === 'basic' && (
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg">
                    <i className="ri-book-open-line text-lg"></i>
                  </div>
                  基礎編（5時間）
                </h3>
                <div className="space-y-4">
                  {[
                    { time: '1時間目', title: 'AIの基本理解と業務への応用可能性', desc: 'ChatGPT・Geminiの仕組みと、御社業務での活用イメージ' },
                    { time: '2時間目', title: 'プロンプト設計の基礎', desc: '指示の出し方、精度を上げる質問の型' },
                    { time: '3時間目', title: '実務文書の作成演習', desc: '提案書・報告書・メール文の自動生成' },
                    { time: '4時間目', title: 'データ整理と分析の基礎', desc: '数字の読み方、要点抽出、意思決定への活用' },
                    { time: '5時間目', title: '社内展開のための型化', desc: 'テンプレート作成、チーム共有の方法' }
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4 p-4 bg-teal-50 rounded-lg">
                      <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center bg-teal-600 text-white rounded-lg font-bold">
                        {item.time}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'advanced' && (
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 text-emerald-600 rounded-lg">
                    <i className="ri-rocket-line text-lg"></i>
                  </div>
                  実践応用編（3時間）
                </h3>
                <div className="space-y-4">
                  {[
                    { time: '6時間目', title: '御社業務に特化したAI活用設計', desc: '事前ヒアリング内容をもとに、実際の業務フローでの活用方法を設計' },
                    { time: '7時間目', title: '業務アプリの作成実習', desc: 'ChatGPT/Gemini上で動く入力→出力フォームの構築' },
                    { time: '8時間目', title: '現場定着のための運用設計', desc: '社内ルール化、教育資料の作成、継続改善の仕組み' }
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4 p-4 bg-emerald-50 rounded-lg">
                      <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center bg-emerald-600 text-white rounded-lg font-bold">
                        {item.time}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="w-6 h-6 flex items-center justify-center text-yellow-600">
                  <i className="ri-star-line text-xl"></i>
                </div>
                <strong className="text-lg text-gray-900">事前ヒアリングで御社専用にカスタマイズ</strong>
              </div>
              <p className="text-sm text-gray-600">
                業種・業務内容・課題に合わせて、最適なカリキュラムを組み立てます
              </p>
            </div>
          </div>

          {/* Kuukan Lab Method */}
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="text-center mb-12">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                空間Labメソッド
              </h3>
              <p className="text-lg text-gray-600">
                忘れにくく、再現性の高い3ステップ学習法
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: 'STEP 1',
                  title: '現状業務の分解',
                  desc: '今の業務を「誰が・何を・どう」やっているかを可視化。AIに任せられる部分を明確にします。',
                  icon: 'ri-layout-grid-line',
                  color: 'teal'
                },
                {
                  step: 'STEP 2',
                  title: 'AIに渡す設計',
                  desc: '分解した業務をAIが理解できる形に変換。プロンプトの型を作り、精度を高めます。',
                  icon: 'ri-settings-3-line',
                  color: 'emerald'
                },
                {
                  step: 'STEP 3',
                  title: '現場定着の型化',
                  desc: '作った仕組みをチーム全体で使えるようテンプレート化。継続的に改善できる状態を作ります。',
                  icon: 'ri-team-line',
                  color: 'teal'
                }
              ].map((item, index) => (
                <div key={index} className="relative">
                  <div className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 rounded-2xl p-6 h-full`}>
                    <div className={`w-12 h-12 flex items-center justify-center bg-${item.color}-600 text-white rounded-lg mb-4`}>
                      <i className={`${item.icon} text-2xl`}></i>
                    </div>
                    <div className={`text-sm font-bold text-${item.color}-600 mb-2`}>{item.step}</div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                  {index < 2 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-gray-300">
                      <i className="ri-arrow-right-line text-3xl"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              補助金での費用イメージ
            </h2>
            <p className="text-lg text-gray-600">
              大阪府リスキリング支援補助金（補助率75%）を活用
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 border-2 border-teal-500 shadow-lg">
              <div className="text-center mb-6">
                <div className="inline-block bg-teal-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                  1-5名プラン
                </div>
                <div className="text-5xl font-bold text-gray-900 mb-2">23.75万円</div>
                <div className="text-lg text-gray-600 line-through mb-1">通常価格：95万円</div>
                <div className="text-sm text-teal-700 font-medium">実質負担額（補助率75%適用後）</div>
              </div>
              <div className="space-y-3 mb-6">
                {[
                  '基礎5時間＋応用3時間',
                  '事前ヒアリング無料',
                  '御社専用カスタマイズ',
                  '研修後1ヶ月メールサポート',
                  '補助金申請サポート無料'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center text-teal-600">
                      <i className="ri-check-line text-lg font-bold"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <button 
                onClick={scrollToForm}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-lg font-bold transition-colors whitespace-nowrap"
              >
                このプランで相談する
              </button>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-8 border-2 border-emerald-500 shadow-lg">
              <div className="text-center mb-6">
                <div className="inline-block bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                  6-10名プラン
                </div>
                <div className="text-5xl font-bold text-gray-900 mb-2">33.75万円</div>
                <div className="text-lg text-gray-600 line-through mb-1">通常価格：135万円</div>
                <div className="text-sm text-emerald-700 font-medium">実質負担額（補助率75%適用後）</div>
              </div>
              <div className="space-y-3 mb-6">
                {[
                  '基礎5時間＋応用3時間',
                  '事前ヒアリング無料',
                  '御社専用カスタマイズ',
                  '研修後1ヶ月メールサポート',
                  '補助金申請サポート無料'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center text-emerald-600">
                      <i className="ri-check-line text-lg font-bold"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <button 
                onClick={scrollToForm}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-lg font-bold transition-colors whitespace-nowrap"
              >
                このプランで相談する
              </button>
            </div>
          </div>

          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 max-w-3xl mx-auto">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center text-red-600 flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">補助金に関する重要なお知らせ</h4>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• 申請期限：令和8年3月9日まで受付中</li>
                  <li>• 予算上限に達した場合、期限前でも受付終了となります</li>
                  <li>• 補助金の適用には一定の条件があります（詳細は無料相談時にご説明）</li>
                  <li>• 大阪府内に事業所を持つ中小企業が対象です</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flow Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              導入までの流れ
            </h2>
            <p className="text-lg text-gray-600">
              お申込みから研修実施、アフターフォローまで
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {[
                {
                  step: '01',
                  title: 'お問い合わせ・無料相談',
                  desc: 'フォームまたはお電話でお気軽にご相談ください。補助金の適用可否や研修内容について詳しくご説明します。',
                  icon: 'ri-phone-line'
                },
                {
                  step: '02',
                  title: '事前ヒアリング（無料）',
                  desc: '御社の業種・業務内容・課題をお聞きし、最適なカリキュラムを設計します。補助金申請のサポートもこの段階で開始します。',
                  icon: 'ri-discuss-line'
                },
                {
                  step: '03',
                  title: '研修実施（8時間）',
                  desc: '基礎5時間＋応用3時間の研修を実施。オンライン・対面どちらでも対応可能です。',
                  icon: 'ri-presentation-line'
                },
                {
                  step: '04',
                  title: '1ヶ月メールサポート',
                  desc: '研修後も1ヶ月間、メールでのご質問・ご相談に対応。社内展開の進め方もアドバイスします。',
                  icon: 'ri-customer-service-2-line'
                }
              ].map((item, index) => (
                <div key={index} className="relative">
                  <div className="flex gap-6 bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-xl font-bold text-xl">
                        {item.step}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg">
                          <i className={`${item.icon} text-lg`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                      </div>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                  {index < 3 && (
                    <div className="flex justify-center py-2">
                      <div className="w-0.5 h-6 bg-teal-300"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              受講者の声
            </h2>
            <p className="text-lg text-gray-600">
              様々な業種の方々に実務で活用いただいています
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 text-teal-600 rounded-full font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{item.industry}</div>
                    <div className="text-sm text-gray-600">{item.role}</div>
                  </div>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">「{item.text}」</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              よくある質問
            </h2>
            <p className="text-lg text-gray-600">
              お客様からよくいただくご質問にお答えします
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-8 h-8 flex items-center justify-center bg-teal-100 text-teal-600 rounded-lg flex-shrink-0">
                      <i className="ri-question-line text-lg"></i>
                    </div>
                    <span className="font-bold text-gray-900">{faq.q}</span>
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center text-gray-400 flex-shrink-0 ml-4">
                    <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-xl`}></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <div className="pl-12 text-gray-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA & Contact Form */}
      <section id="contact-form" className="py-20 bg-gradient-to-br from-teal-600 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              まずは無料相談から
            </h2>
            <p className="text-lg mb-6">
              補助金の適用可否や研修内容について、お気軽にご相談ください
            </p>
            <div className="inline-flex items-center gap-2 bg-red-500 text-white px-6 py-3 rounded-full font-bold">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-alarm-warning-line text-lg"></i>
              </div>
              <span>予算上限で早期終了の可能性があります</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl">
            <form onSubmit={handleSubmit} data-readdy-form>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="株式会社〇〇"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    担当者名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="山田 太郎"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="example@company.co.jp"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="06-1234-5678"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    業種 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  >
                    <option value="">選択してください</option>
                    <option value="製造業">製造業</option>
                    <option value="建設業">建設業</option>
                    <option value="飲食業">飲食業</option>
                    <option value="小売業">小売業</option>
                    <option value="サービス業">サービス業</option>
                    <option value="IT・通信">IT・通信</option>
                    <option value="医療・福祉">医療・福祉</option>
                    <option value="不動産">不動産</option>
                    <option value="その他">その他</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    従業員規模 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="employeeSize"
                    value={formData.employeeSize}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  >
                    <option value="">選択してください</option>
                    <option value="1-10名">1-10名</option>
                    <option value="11-50名">11-50名</option>
                    <option value="51-100名">51-100名</option>
                    <option value="101-300名">101-300名</option>
                    <option value="301名以上">301名以上</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    受講希望人数 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="participants"
                    value={formData.participants}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  >
                    <option value="">選択してください</option>
                    <option value="1-5名">1-5名</option>
                    <option value="6-10名">6-10名</option>
                    <option value="11名以上">11名以上（要相談）</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    研修希望時期
                  </label>
                  <input
                    type="text"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="例：2025年5月頃"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  相談内容（自由記述）
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="補助金について詳しく知りたい、具体的な業務課題を相談したい、など"
                ></textarea>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500文字
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-300 rounded-lg text-green-700 text-sm">
                  送信が完了しました。担当者より2営業日以内にご連絡いたします。
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm">
                  送信に失敗しました。お手数ですが、お電話でお問い合わせください。
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white py-4 rounded-lg font-bold text-lg transition-colors whitespace-nowrap"
              >
                {isSubmitting ? '送信中...' : '無料相談を申し込む'}
              </button>
            </form>

            <div className="mt-8 pt-8 border-t border-gray-200 text-center">
              <p className="text-gray-700 font-medium mb-3">お電話でのお問い合わせ</p>
              <a href="tel:0661231122" className="inline-flex items-center gap-3 text-2xl font-bold text-teal-600 hover:text-teal-700 transition-colors">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-phone-line text-2xl"></i>
                </div>
                <span>06-6123-1122</span>
              </a>
              <p className="text-sm text-gray-600 mt-2">平日9:00-18:00</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/fb59e5af169faed257c467831b86fc31/6b5b44d6abb76df92ded18f8f997228f.png" 
                alt="株式会社空間Lab" 
                className="h-10 w-auto mb-4"
              />
              <p className="text-sm text-gray-400">
                大阪府補助金で実現する<br />企業AI研修サービス
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">お問い合わせ</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p>TEL: 06-6123-1122</p>
                <p>平日9:00-18:00</p>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4">サービス内容</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>基礎5時間＋応用3時間</li>
                <li>事前ヒアリング無料</li>
                <li>補助金申請サポート</li>
                <li>1ヶ月メールサポート</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 株式会社空間Lab. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block mt-2 text-gray-500 hover:text-gray-300 transition-colors"
            >
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
